import { Logger, TokenInspector, DateHelpers } from "../utils";
import { TokenCache } from "../storage";

var LicensingService =
/** @class */
function () {
  function LicensingService(config) {
    this._HttpClientToUse = config.httpClient;
    this._isProxyRequest = config.proxyRequestHost !== undefined;

    if (this._isProxyRequest) {
      this._tokenUriEndpoint = "https://" + config.proxyRequestHost + "/.license-token";
    } else {
      this._tokenUriEndpoint = "https://" + config.environment.hostname + "/license-token";
    }

    this._licensingEnvironment = config.environment;
    this._logger = new Logger(typeof config.enableLogging !== undefined ? config.enableLogging : false);
    this._tokenCache = new TokenCache(this._logger);
    this._tokenInspector = new TokenInspector(this._logger);
  }
  /**
   * Verifies if the specified (validated) ILicenseToken instance contains license information about the requested product.
   * @param token ILicenseToken instance
   * @param product Name of the product
   * @param requiredFeatures Array of features to check for
   * @param userIdentifier User identifier represented by a GUID value
   */


  LicensingService.prototype.VerifyToken = function (token, product, requiredFeatures, userIdentifier) {
    var parsedToken = this._tokenInspector.Parse(token.jwt);

    if (parsedToken !== undefined) {
      if (parsedToken["http://schemas.microsoft.com/identity/claims/objectidentifier"] !== userIdentifier) {
        this._logger.LogInfo("Parsed token is not currently signed in user.");

        return undefined;
      }

      var licensedProducts = parsedToken.pro;
      var foundLicensedProducts = licensedProducts.filter(function (x) {
        return x.product.name.toLowerCase() === product.toLowerCase() && DateHelpers.isTodayBetween(x.validFrom, x.validTo);
      });

      if (foundLicensedProducts.length > 0) {
        if (foundLicensedProducts.length > 1) {
          this._logger.LogWarning("Multiple active product licenses found for user/tenant. By default, the first one is selected.");
        } else {
          this._logger.LogInfo("Single valid product license found for user/tenant.");
        }

        var selectedProduct_1 = foundLicensedProducts[0];

        if (requiredFeatures && requiredFeatures.length > 0) {
          var allFeaturesPresent = requiredFeatures.every(function (feature) {
            return selectedProduct_1.product.features.indexOf(feature) >= 0;
          });

          if (allFeaturesPresent) {
            return selectedProduct_1;
          } else {
            this._logger.LogInfo("No valid product license was found containing the required features [" + requiredFeatures.map(function (x) {
              return "'" + x + "'";
            }).join(", ") + "]");
          }
        } else {
          this._logger.LogInfo("A valid product license was found but no feature check was performed because no required feature names where passed.");

          return selectedProduct_1;
        }
      }
    }

    this._logger.LogInfo("No product license found for user/tenant.");

    return undefined;
  };
  /**
   * Gets a license token from the licensing service.
   */


  LicensingService.prototype.GetToken = function () {
    var _this = this;

    return new Promise(function (resolve, reject) {
      _this._logger.LogInfo("Acquiring new token using endpoint '" + _this._tokenUriEndpoint + "'");

      try {
        _this._HttpClientToUse.Get(_this._tokenUriEndpoint).then(function (token) {
          resolve(token);
        }, function (error) {
          reject(error);
        });
      } catch (error) {
        _this._logger.LogError("Error occured when acquiring token from licensing service.", error);

        reject(error);
      }
    });
  };
  /**
   * Returns a ILicensedProduct instance if a valid license was found, else undefined.
   * @param product Name of the product.
   * @param userIdentifier String containing a unique value identifying the user.
   */


  LicensingService.prototype.AcquireLicenseForProduct = function (product, userIdentifier) {
    return this.AcquireLicenseForProductAndFeatures(product, undefined, userIdentifier);
  };
  /**
   * Returns a ILicensedProduct instance if a valid license was found, else undefined.
   * @param product Name of the product.
   * @param requiredFeatures Array of strings containing the required features for the product.
   * @param userIdentifier String containing a unique value identifying the user.
   */


  LicensingService.prototype.AcquireLicenseForProductAndFeatures = function (product, requiredFeatures, userIdentifier) {
    var _this = this;

    var cachedToken = this._tokenCache.Get(userIdentifier);

    var licensedProduct = undefined;
    var tokenResolver = null; // When no cached version is available (or when the token is expired => invalid), we grab a new version from the licensing service

    if (!cachedToken || !this._tokenInspector.Validate(cachedToken.jwt, cachedToken.expiration)) {
      tokenResolver = this.GetToken();
    } else {
      tokenResolver = Promise.resolve(cachedToken);
    }

    return new Promise(function (resolve, reject) {
      tokenResolver.then(function (resolvedToken) {
        if (!resolvedToken) {
          _this._logger.LogError("No license token information available.");

          return {
            environment: _this._licensingEnvironment,
            isValid: false,
            product: undefined,
            hasFeature: function hasFeature(featureName) {
              return false;
            } // this is allways null

          };
        }

        if (_this._tokenInspector.Validate(resolvedToken.jwt, resolvedToken.expiration)) {
          licensedProduct = _this.VerifyToken(resolvedToken, product, requiredFeatures, userIdentifier);

          if (licensedProduct !== undefined) {
            _this._tokenCache.Set(userIdentifier, resolvedToken);

            if (requiredFeatures && requiredFeatures.length > 0) {
              _this._logger.LogInfo("Validated and verified token is available for product '" + product + "' with features [" + requiredFeatures.map(function (x) {
                return "'" + x + "'";
              }).join(", ") + "]");
            } else {
              _this._logger.LogInfo("Validated and verified token is available for product '" + product + "'");
            }
          } else {
            _this._tokenCache.Remove(userIdentifier);

            _this._logger.LogWarning("License token is not valid for product '" + product + "'");
          }
        } else {
          _this._tokenCache.Remove(userIdentifier);
        }

        resolve({
          environment: _this._licensingEnvironment,
          isValid: licensedProduct !== undefined,
          product: licensedProduct,
          hasFeature: function hasFeature(featureName) {
            if (licensedProduct && licensedProduct.product && licensedProduct.product.features) {
              return licensedProduct.product.features.indexOf(featureName) >= 0;
            }

            return false;
          }
        });
      });
    });
  };

  return LicensingService;
}();

export { LicensingService };